import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { BadgeCheck, Download, Plus, KeyRound, UserMinus, Pencil, RotateCcw } from 'lucide-react';
import { useConfirm } from '../../components/ConfirmDialog';
import { Panel, Field, FormRow, fmtDate } from './design';

// The screening register (employees, contractors, volunteers, responsible persons, workers engaged by
// self-managing participants), checks applied for and pending with their conditions, and access to the
// government systems: offboarding when someone leaves, and a review every six months.

const isoToday = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
const plus = (ymd, n) => { const d = new Date(`${ymd}T00:00:00Z`); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };
const cap = (s) => String(s || '').replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
const TONE = { current: 'text-emerald-700', expiring: 'text-amber-700', expired: 'text-red-800', pending: 'text-amber-700', 'no expiry recorded': 'text-amber-700', 'none recorded': 'text-slate-500' };
const CHECKS = [['police_check', 'Police check'], ['ndis_worker_screening', 'NDIS Worker Screening'], ['aged_care_worker_screening', 'Aged care worker screening']];
const CHECK_LABEL = Object.fromEntries(CHECKS);
// A responsible person's check is stored as its key ('police_check'); a qualification from Staff
// documents is stored by its name. Either way the register shows words, never the key.
const checkLabel = (v) => CHECK_LABEL[v] || (/^[a-z0-9_]+$/.test(String(v || '')) ? cap(v) : (v || 'Screening'));

async function download(url, name) {
  try {
    const r = await api.get(url, { responseType: 'blob' });
    const href = window.URL.createObjectURL(r.data);
    const a = document.createElement('a'); a.href = href; a.download = name; a.click(); window.URL.revokeObjectURL(href);
  } catch (e) {
    let msg = 'Could not download it';
    try { msg = JSON.parse(await e.response.data.text()).message || msg; } catch { /* not JSON */ }
    toast.error(msg);
  }
}

// A failed load says so in the panel, with a way to try again, rather than showing an empty list.
function LoadError({ message, onRetry }) {
  return (
    <p className="flex flex-wrap items-center gap-2 py-1 text-[12.5px] text-red-800" role="alert">
      {message}
      <button type="button" className="btn-secondary btn-sm" onClick={onRetry}>Try again</button>
    </p>
  );
}

// A check type select that keeps a stored type outside the three (typed elsewhere) as its own option,
// so opening and saving never swaps it for the first in the list.
function CheckSelect({ id, value, onChange }) {
  return (
    <select id={id} className="input-field" value={value} onChange={(e) => onChange(e.target.value)}>
      {value && !CHECK_LABEL[value] && <option value={value}>{checkLabel(value)}</option>}
      {CHECKS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
    </select>
  );
}

export function ScreeningRegister({ className = 'mt-5' }) {
  const confirm = useConfirm();
  const [d, setD] = useState({ rows: [], allowPending: false });
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [kind, setKind] = useState('');
  const [pend, setPend] = useState(null); // a pending check being added (no id) or changed (id)
  const [kp, setKp] = useState(null); // a responsible person being added (no id) or changed (id)
  const load = useCallback(() => {
    setLoadError(null);
    api.get('/aged-care/compliance/screening')
      .then((r) => { setD({ rows: r.data?.rows || [], allowPending: !!r.data?.allowPending }); setLoaded(true); })
      .catch((e) => setLoadError(e.response?.data?.message || 'Could not load the screening register'));
  }, []);
  useEffect(() => { load(); }, [load]);
  // The settings are one row: the current row is read first and sent back whole with this one
  // change, so the Code of Conduct settings are left as they are.
  const toggle = async (on) => {
    try { await api.put('/aged-care/compliance/settings', { ...(await api.get('/aged-care/compliance/settings')).data, allow_pending_screening: on }); toast.success('Saved'); load(); }
    catch (e) { toast.error(e.response?.data?.message || 'Could not save the screening policy'); }
  };
  const savePend = async () => {
    const body = { checkType: pend.checkType, appliedOn: pend.appliedOn, validUntil: pend.validUntil, reference: pend.reference, conditions: pend.conditions };
    try {
      if (pend.id) await api.put(`/aged-care/compliance/screening-pending/${pend.id}`, body);
      else await api.post('/aged-care/compliance/screening-pending', { staffId: pend.staffId, ...body });
      toast.success(pend.id ? 'Pending check updated' : 'Pending check recorded');
      setPend(null); load();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not save the pending check'); }
  };
  const clearPend = async (p, who) => {
    const ok = await confirm(
      `The pending ${checkLabel(p.check_type).toLowerCase()} for ${who} comes off the register, and they no longer work under its conditions. If it was issued, record the check itself on their Staff documents page.`,
      { title: 'Mark as issued or withdrawn?', confirmLabel: 'Mark as issued or withdrawn' },
    );
    if (!ok) return;
    try { await api.post(`/aged-care/compliance/screening-pending/${p.id}/clear`); toast.success('No longer pending'); load(); }
    catch (e) { toast.error(e.response?.data?.message || 'Could not save'); }
  };
  const saveKp = async () => {
    if (!kp.name.trim()) return toast.error('Give their name');
    const body = { name: kp.name.trim(), position: kp.position, screeningType: kp.screeningType, screeningRef: kp.screeningRef, screeningExpires: kp.screeningExpires };
    try {
      if (kp.id) await api.put(`/aged-care/compliance/key-personnel/${kp.id}`, body);
      else await api.post('/aged-care/compliance/key-personnel', body);
      toast.success(kp.id ? 'Responsible person updated' : 'Responsible person added');
      setKp(null); load();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not save the responsible person'); }
  };
  // Not deleted: marked inactive, so the record of their screening is kept.
  const endKp = async (r) => {
    const ok = await confirm(`${r.name} comes off the screening register as a responsible person. Their record is kept.`, { title: 'No longer a responsible person?', confirmLabel: 'Take off the register', danger: true });
    if (!ok) return;
    try { await api.put(`/aged-care/compliance/key-personnel/${r.id}`, { active: false }); toast.success(`${r.name} is no longer a responsible person`); load(); }
    catch (e) { toast.error(e.response?.data?.message || 'Could not save'); }
  };
  const editPend = (p) => { setKp(null); setPend({ id: p.id, checkType: p.check_type, appliedOn: p.applied_on || '', validUntil: p.valid_until || '', reference: p.reference || '', conditions: p.conditions || '' }); };
  const editKp = (r) => {
    const c = r.checks[0] || {};
    setPend(null);
    setKp({ id: r.id, name: r.name || '', position: r.role || '', screeningType: c.name || 'police_check', screeningRef: c.ref || '', screeningExpires: c.expires || '' });
  };
  const kinds = [...new Set(d.rows.map((r) => r.kind))];
  const rows = d.rows.filter((r) => !kind || r.kind === kind);
  const staff = d.rows.filter((r) => ['Employee', 'Contractor', 'Volunteer'].includes(r.kind));

  const pendForm = pend && (
    <div className="rounded-lg border border-slate-200 p-3 space-y-2 mt-1.5">
      <FormRow cols={2}>
        {!pend.id && <Field label="Worker">{(id) => <select id={id} className="input-field" value={pend.staffId} onChange={(e) => setPend((p) => ({ ...p, staffId: e.target.value }))}><option value="">Choose</option>{staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select>}</Field>}
        <Field label="Check">{(id) => <CheckSelect id={id} value={pend.checkType} onChange={(v) => setPend((p) => ({ ...p, checkType: v }))} />}</Field>
      </FormRow>
      <FormRow cols={3}>
        <Field label="Applied on">{(id) => <input id={id} type="date" className="input-field" value={pend.appliedOn} onChange={(e) => setPend((p) => ({ ...p, appliedOn: e.target.value }))} />}</Field>
        <Field label="May work until" hint="At most 120 days from applying.">{(id) => <input id={id} type="date" className="input-field" value={pend.validUntil} onChange={(e) => setPend((p) => ({ ...p, validUntil: e.target.value }))} />}</Field>
        <Field label="Application reference">{(id) => <input id={id} className="input-field" value={pend.reference} onChange={(e) => setPend((p) => ({ ...p, reference: e.target.value }))} />}</Field>
      </FormRow>
      <Field label="Conditions while pending">{(id) => <input id={id} className="input-field" value={pend.conditions} onChange={(e) => setPend((p) => ({ ...p, conditions: e.target.value }))} placeholder="For example, supervised, never alone with a participant" />}</Field>
      <div className="flex justify-end gap-2"><button type="button" className="btn-ghost btn-sm" onClick={() => setPend(null)}>Cancel</button><button type="button" className="btn-primary btn-sm" onClick={savePend}>Save</button></div>
    </div>
  );
  const kpForm = kp && (
    <div className="rounded-lg border border-slate-200 p-3 space-y-2 mt-1.5">
      <FormRow cols={2}>
        <Field label="Name">{(id) => <input id={id} className="input-field" value={kp.name} onChange={(e) => setKp((p) => ({ ...p, name: e.target.value }))} />}</Field>
        <Field label="Position">{(id) => <input id={id} className="input-field" value={kp.position} onChange={(e) => setKp((p) => ({ ...p, position: e.target.value }))} placeholder="Director, CEO, operations manager" />}</Field>
      </FormRow>
      <FormRow cols={3}>
        <Field label="Check">{(id) => <CheckSelect id={id} value={kp.screeningType} onChange={(v) => setKp((p) => ({ ...p, screeningType: v }))} />}</Field>
        <Field label="Number">{(id) => <input id={id} className="input-field" value={kp.screeningRef} onChange={(e) => setKp((p) => ({ ...p, screeningRef: e.target.value }))} />}</Field>
        <Field label="Expires">{(id) => <input id={id} type="date" className="input-field" value={kp.screeningExpires} onChange={(e) => setKp((p) => ({ ...p, screeningExpires: e.target.value }))} />}</Field>
      </FormRow>
      <div className="flex justify-end gap-2"><button type="button" className="btn-ghost btn-sm" onClick={() => setKp(null)}>Cancel</button><button type="button" className="btn-primary btn-sm" onClick={saveKp}>Save</button></div>
    </div>
  );

  return (
    <Panel title="Screening register" icon={BadgeCheck} className={className} bodyClass="px-4 py-3 space-y-2 text-[13px]"
      actions={(
        <span className="flex gap-2">
          <select aria-label="Who" className="input-field !w-auto !py-1" value={kind} onChange={(e) => setKind(e.target.value)}><option value="">Everyone</option>{kinds.map((k) => <option key={k} value={k}>{k}</option>)}</select>
          <button type="button" className="btn-secondary btn-sm" onClick={() => download('/aged-care/compliance/screening-register.csv', `screening-register-${isoToday()}.csv`)}><Download size={13} /> Export</button>
        </span>
      )}>
      <p className="text-[12px] text-slate-500">Everyone who works with participants: employees, contractors, volunteers, responsible persons and workers a self-managing participant engages. A police certificate is valid 3 years and NDIS Worker Screening 5 years from issue when no expiry is recorded.</p>
      {loadError ? <LoadError message={loadError} onRetry={load} /> : !loaded ? (
        <div className="space-y-2 py-1" aria-busy="true" aria-label="Loading the screening register">
          <div className="skeleton h-5 rounded" /><div className="skeleton h-5 rounded" /><div className="skeleton h-5 rounded w-2/3" />
        </div>
      ) : (
        <>
          <label className="flex items-center gap-2 text-[12.5px] text-slate-700">
            <input type="checkbox" checked={!!d.allowPending} onChange={(e) => toggle(e.target.checked)} /> Let a worker start while a check they applied for is pending, under the conditions recorded (your screening policy)
          </label>
          {rows.length === 0 ? (
            <p className="text-slate-500 py-1">Nobody on the register yet. Active staff appear here on their own; add responsible persons below.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {rows.map((r) => {
                const isKp = r.kind === 'Responsible person';
                return (
                  <li key={`${r.kind}-${r.id}`} className="py-1.5">
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <span><span className="font-medium text-slate-800">{r.name}</span> <span className="text-[12px] text-slate-500">{r.kind}{r.role ? ` · ${cap(r.role)}` : ''}</span></span>
                      <span className="flex flex-wrap items-center gap-1">
                        <span className={`text-[12px] ${TONE[r.status] || ''}`}>{cap(r.status)}</span>
                        {isKp && kp?.id !== r.id && (
                          <>
                            <button type="button" className="btn-ghost btn-sm" aria-label={`Edit ${r.name}`} onClick={() => editKp(r)}><Pencil size={13} /> Edit</button>
                            <button type="button" className="btn-ghost btn-sm" onClick={() => endKp(r)}>No longer a responsible person</button>
                          </>
                        )}
                      </span>
                    </div>
                    {r.checks.map((c, i) => <p key={i} className={`text-[12px] ${TONE[c.status] || 'text-slate-600'}`}>{checkLabel(c.name)}{c.ref ? ` ${c.ref}` : ''} · {c.expires ? `${c.status === 'expired' ? 'expired' : 'expires'} ${fmtDate(c.expires)}${c.from_issue ? ' (from the issue date)' : ''}` : 'no expiry recorded'}</p>)}
                    {r.pending.map((p) => (
                      <div key={p.id}>
                        <p className="flex flex-wrap items-center gap-x-2 text-[12px] text-amber-800">
                          <span>Pending {checkLabel(p.check_type).toLowerCase()}, applied {fmtDate(p.applied_on)}, may work until {fmtDate(p.valid_until)}{p.reference ? `, reference ${p.reference}` : ''}: {p.conditions}</span>
                          {pend?.id !== p.id && (
                            <span className="flex gap-1">
                              <button type="button" className="btn-ghost btn-sm" onClick={() => editPend(p)}><Pencil size={13} /> Edit</button>
                              <button type="button" className="btn-ghost btn-sm" onClick={() => clearPend(p, r.name)}>Mark as issued or withdrawn</button>
                            </span>
                          )}
                        </p>
                        {pend?.id === p.id && pendForm}
                      </div>
                    ))}
                    {isKp && kp?.id === r.id && kpForm}
                  </li>
                );
              })}
            </ul>
          )}
          <div className="flex flex-wrap gap-2">
            {!pend && <button type="button" className="btn-ghost btn-sm" onClick={() => { setKp(null); setPend({ staffId: '', checkType: 'police_check', appliedOn: isoToday(), validUntil: plus(isoToday(), 60), reference: '', conditions: '' }); }}><Plus size={13} /> Record a check applied for</button>}
            {!kp && <button type="button" className="btn-ghost btn-sm" onClick={() => { setPend(null); setKp({ name: '', position: '', screeningType: 'police_check', screeningRef: '', screeningExpires: '' }); }}><Plus size={13} /> Add a responsible person</button>}
          </div>
          {pend && !pend.id && pendForm}
          {kp && !kp.id && kpForm}
        </>
      )}
    </Panel>
  );
}

export function GovAccess({ className = 'mt-5' }) {
  const [off, setOff] = useState({ items: [], people: [] });
  const [rev, setRev] = useState({ reviews: [], nextDue: null, systems: [] });
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [form, setForm] = useState(null);
  const [noting, setNoting] = useState(null); // { staffId, key, note } while a tick's note is being typed
  const [showFinished, setShowFinished] = useState(false);
  const load = useCallback(() => {
    setLoadError(null);
    Promise.all([api.get('/aged-care/access/offboarding'), api.get('/aged-care/access/reviews')])
      .then(([o, r]) => {
        setOff({ items: o.data?.items || [], people: o.data?.people || [] });
        setRev({ reviews: r.data?.reviews || [], nextDue: r.data?.nextDue || null, systems: r.data?.systems || [] });
        setLoaded(true);
      })
      .catch((e) => setLoadError(e.response?.data?.message || 'Could not load access to the government systems'));
  }, []);
  useEffect(() => { load(); }, [load]);
  const tick = async (p, key, { notApplicable = false, note = '' } = {}) => {
    try {
      const r = await api.post(`/aged-care/access/offboarding/${p.staff_id}/check`, { key, ...(notApplicable ? { notApplicable: true } : {}), ...(note.trim() ? { note: note.trim() } : {}) });
      setNoting(null);
      toast.success(r.data?.complete ? `${p.name} is offboarded from every system` : 'Ticked');
      load();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not save the tick'); }
  };
  // A tick made by mistake comes off; someone who was finished goes back on the list.
  const untick = async (p, key) => {
    try { await api.post(`/aged-care/access/offboarding/${p.staff_id}/check`, { key, undo: true }); toast.success('Tick taken off'); load(); }
    catch (e) { toast.error(e.response?.data?.message || 'Could not take the tick off'); }
  };
  const saveReview = async () => {
    try { await api.post('/aged-care/access/reviews', { systems: form.systems, note: form.note }); setForm(null); toast.success('Access review recorded'); load(); }
    catch (e) { toast.error(e.response?.data?.message || 'Could not record the review'); }
  };
  const open = off.people.filter((p) => !p.completed_at);
  const finished = off.people.filter((p) => p.completed_at);
  const last = rev.reviews[0];

  const person = (p) => (
    <div key={p.staff_id} className="py-1.5 border-b border-slate-100 last:border-b-0">
      <p className="text-slate-800">{p.name} <span className="text-[12px] text-slate-500">left {fmtDate(p.archived_on)}{p.completed_at ? `, offboarded ${fmtDate(p.completed_at)}` : ''}</span></p>
      {off.items.map((it) => {
        const done = (p.items || {})[it.key];
        const isNoting = noting && noting.staffId === p.staff_id && noting.key === it.key;
        return (
          <div key={it.key} className="text-[12.5px] py-0.5">
            <p className="flex flex-wrap items-center gap-2">
              <span className={done ? 'text-emerald-700' : 'text-slate-700'}>
                <span aria-hidden="true">{done ? '✓' : '○'}</span><span className="sr-only">{done ? 'Done: ' : 'To do: '}</span> {it.label}
                {done && done.note ? ` (${done.note})` : ''}{done && done.at && !done.auto ? `, ${fmtDate(done.at)}` : ''}
              </span>
              {it.key !== 'rostery' && (done ? (
                <button type="button" className="btn-ghost btn-sm" onClick={() => untick(p, it.key)}><RotateCcw size={13} /> Undo</button>
              ) : !isNoting && (
                <>
                  <button type="button" className="btn-ghost btn-sm" onClick={() => setNoting({ staffId: p.staff_id, key: it.key, note: '' })}>Mark removed</button>
                  <button type="button" className="btn-ghost btn-sm" title="They never had access to this system" onClick={() => tick(p, it.key, { notApplicable: true })}>Mark as not applicable</button>
                </>
              ))}
            </p>
            {isNoting && (
              <div className="flex flex-wrap items-end gap-2 mt-1 mb-1.5">
                <Field label="Note (optional)" className="flex-1 min-w-[12rem]">
                  {(id) => (
                    <input id={id} className="input-field" value={noting.note} autoFocus
                      onChange={(e) => { const v = e.target.value; setNoting((s) => (s ? { ...s, note: v } : s)); }}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); tick(p, it.key, { note: noting.note }); } }}
                      placeholder="For example, removed by the DEX helpdesk, ticket 1234" />
                  )}
                </Field>
                <button type="button" className="btn-ghost btn-sm" onClick={() => setNoting(null)}>Cancel</button>
                <button type="button" className="btn-primary btn-sm" onClick={() => tick(p, it.key, { note: noting.note })}>Mark removed</button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  return (
    <Panel title="Access to the government systems" icon={KeyRound} className={className} bodyClass="px-4 py-3 space-y-3 text-[13px]">
      {loadError ? <LoadError message={loadError} onRetry={load} /> : !loaded ? (
        <div className="space-y-2 py-1" aria-busy="true" aria-label="Loading access to the government systems">
          <div className="skeleton h-5 rounded" /><div className="skeleton h-5 rounded w-2/3" />
        </div>
      ) : (
        <>
          <div>
            <p className="font-medium text-slate-800 flex items-center gap-1.5"><UserMinus size={14} /> People who left</p>
            {open.length === 0 ? <p className="text-[12.5px] text-slate-500">Nobody waiting to be offboarded.</p> : open.map(person)}
            {finished.length > 0 && (
              <div className="mt-1">
                <button type="button" className="btn-ghost btn-sm" aria-expanded={showFinished} onClick={() => setShowFinished((v) => !v)}>
                  {showFinished ? 'Hide' : 'Show'} the {finished.length} offboarded in the last year
                </button>
                {showFinished && finished.map(person)}
              </div>
            )}
          </div>
          <div>
            <div className="flex items-center justify-between">
              <p className="font-medium text-slate-800">Who has access: reviewed every six months</p>
              {!form && <button type="button" className="btn-secondary btn-sm" onClick={() => setForm({ systems: rev.systems.map((s) => ({ system: s.label, people: '', changes: '' })), note: '' })}>Review now</button>}
            </div>
            <p className="text-[12.5px] text-slate-600">{last ? `Last reviewed ${fmtDate(last.reviewed_on)}${last.reviewed_by_name ? ` by ${last.reviewed_by_name}` : ''}; next by ${fmtDate(rev.nextDue)}.` : 'Not reviewed yet.'}</p>
            {last && last.note && <p className="text-[12.5px] text-slate-500 whitespace-pre-wrap">Note: {last.note}</p>}
            {form && (
              <div className="rounded-lg border border-slate-200 p-3 space-y-2 mt-2">
                {form.systems.map((s, i) => (
                  <FormRow key={s.system} cols={2}>
                    <Field label={`${s.system}: who has access now`}>{(id) => <input id={id} className="input-field" value={s.people} onChange={(e) => setForm((p) => ({ ...p, systems: p.systems.map((x, j) => (j === i ? { ...x, people: e.target.value } : x)) }))} />}</Field>
                    <Field label="Changes made">{(id) => <input id={id} className="input-field" value={s.changes} onChange={(e) => setForm((p) => ({ ...p, systems: p.systems.map((x, j) => (j === i ? { ...x, changes: e.target.value } : x)) }))} placeholder="None" />}</Field>
                  </FormRow>
                ))}
                <Field label="Note" hint="Optional. Anything the next reviewer should know.">
                  {(id) => <textarea id={id} rows={2} className="input-field" value={form.note} onChange={(e) => { const v = e.target.value; setForm((p) => ({ ...p, note: v })); }} />}
                </Field>
                <div className="flex justify-end gap-2"><button type="button" className="btn-ghost btn-sm" onClick={() => setForm(null)}>Cancel</button><button type="button" className="btn-primary btn-sm" onClick={saveReview}>Record the review</button></div>
              </div>
            )}
          </div>
        </>
      )}
    </Panel>
  );
}
